import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wrench, ArrowRight, Tractor, CalendarClock, FileDown, FolderKanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    { icon: Tractor, label: "Garage", desc: "15+ equipment types pre-loaded. Tractors, airplanes, chainsaws — we've got the schedules." },
    { icon: CalendarClock, label: "Smart schedule", desc: "Auto-suggested tasks by hours & days. Swipe to accept, snooze any time." },
    { icon: FileDown, label: "PDF exports", desc: "Service history packets for resale, insurance, or the IRS." },
    { icon: FolderKanban, label: "Projects", desc: "Bundle work into campaigns like 'Spring Planting'." },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 noise-overlay">
      {/* Nav */}
      <header className="border-b border-zinc-900 sticky top-0 z-40 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 grid place-items-center bg-orange-600 rounded-sm">
              <Wrench className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display font-black text-xl tracking-tighter">MAINTAIN<span className="text-orange-500">HQ</span></div>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/app"><Button className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="go-to-app">Enter shop</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="text-zinc-300 hover:text-orange-500 uppercase tracking-widest text-xs">Sign in</Button></Link>
                <Link to="/login"><Button className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="get-started-btn">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-32">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <div className="inline-block text-[10px] uppercase tracking-[0.3em] text-orange-500 border border-orange-600/40 px-3 py-1.5 rounded-sm mb-6">
                // FOR PEOPLE WHO DO THEIR OWN MAINTENANCE
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.9] mb-6">
                Every bolt.<br />
                Every <span className="text-orange-500">hour.</span><br />
                One logbook.
              </h1>
              <p className="text-base sm:text-xl text-zinc-400 mb-6 max-w-xl leading-relaxed">
                Everything you own needs maintenance. <span className="text-orange-500 font-semibold">Clock in</span> to save every service — in one place.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to={user ? "/app" : "/login"}>
                  <Button className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm py-6 px-8 uppercase tracking-widest font-bold text-sm" data-testid="hero-cta">
                    Open the shop <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="secondary" className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-sm py-6 px-8 uppercase tracking-widest text-xs font-bold">
                    Sign in
                  </Button>
                </Link>
              </div>
              <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-zinc-500 leading-relaxed">
                <span className="text-orange-500">Home appliances</span>
                <span className="mx-2 text-zinc-700">→</span>
                <span>Cars & trucks</span>
                <span className="mx-2 text-zinc-700">→</span>
                <span>Farm</span>
                <span className="mx-2 text-zinc-700">→</span>
                <span>Construction</span>
                <span className="mx-2 text-zinc-700">→</span>
                <span>Recreation</span>
                <span className="mx-2 text-zinc-700">→</span>
                <span className="text-orange-500">Aviation</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                <span>✓ 70+ pre-loaded types</span>
                <span>✓ PDF reports</span>
                <span>✓ Photo & video log</span>
              </div>
            </div>
            <div className="lg:col-span-2 relative">
              <div className="relative aspect-[4/5] bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/3807277/pexels-photo-3807277.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900&h=1200"
                  alt="Mechanic working on car engine in shop"
                  className="absolute inset-0 w-full h-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="font-mono text-[10px] text-orange-500 uppercase tracking-widest mb-1">// LIVE SHOP FLOOR</div>
                  <div className="font-display font-black text-2xl tracking-tight">Logbook online</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-zinc-900 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-4">// PLATFORM · 01</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-12 max-w-2xl">
            Built for the shop, not a spreadsheet.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-800">
            {features.map((f, i) => (
              <div key={f.label} className={`p-8 ${i > 0 ? "border-t md:border-t-0 md:border-l border-zinc-800" : ""}`}>
                <f.icon className="w-8 h-8 text-orange-500 mb-4" />
                <h3 className="font-display font-bold text-xl tracking-tight mb-2">{f.label}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-900">
        <div className="max-w-5xl mx-auto px-6 py-20 lg:py-28 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-4">// NOT ANOTHER MISSED OIL CHANGE</div>
          <h2 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
            Clock in. Get <span className="text-orange-500">organized.</span>
          </h2>
          <p className="text-lg text-zinc-400 mb-8 max-w-xl mx-auto">
            No credit card. No setup fees. Just a shop logbook that actually works.
          </p>
          <Link to={user ? "/app" : "/login"}>
            <Button className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm py-6 px-10 uppercase tracking-widest font-bold text-sm">
              Start logging <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-6 text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          © MaintainHQ · Shop logbook system
        </div>
      </footer>
    </div>
  );
}
